import { useState, useMemo } from "react";
import {
    Wallet, FileText, CheckCircle2, Clock, AlertTriangle, ArrowRightCircle,
    FileCheck2, BookOpen, GraduationCap, Download, Printer, RefreshCw,
    Layers, ArrowRight, Mail, Sparkles, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
    useAdminOutputDecisionRecap,
    exportOutputDecisionsRecapExcel,
    type DecisionFilters,
} from "@/services/adminConferenceOutputService";
import toast from "react-hot-toast";

interface OutputDecisionsRecapTabProps {
    conferences?: Array<{ conference_id: number; name: string; slug?: string }>;
    onDrillDown: (filters: Partial<DecisionFilters>) => void;
}

const formatIDR = (amount: number | null | undefined): string => {
    if (!amount) return "Rp 0";
    return new Intl.NumberFormat("id-ID", {
        style: "currency",
        currency: "IDR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
    }).format(amount);
};

const formatNumber = (num: number | null | undefined): string => {
    if (num === null || num === undefined) return "0";
    return new Intl.NumberFormat("id-ID").format(num);
};

export default function OutputDecisionsRecapTab({ conferences, onDrillDown }: OutputDecisionsRecapTabProps) {
    const [selectedConferenceId, setSelectedConferenceId] = useState<string>("all");
    const [journalSubTab, setJournalSubTab] = useState<"sinta" | "scopus">("sinta");
    const [isExporting, setIsExporting] = useState(false);

    const { data, isLoading, refetch, isFetching } = useAdminOutputDecisionRecap(selectedConferenceId);

    const summary = data?.summary;
    const byPublisher = data?.by_publisher ?? [];
    const byConference = data?.by_conference ?? [];
    const byScopus = data?.by_scopus;
    const bySinta = data?.by_sinta;

    const handleExportExcel = async () => {
        try {
            setIsExporting(true);
            toast.loading("Menyiapkan file Excel Rekap Eksekutif...", { id: "export-recap" });
            await exportOutputDecisionsRecapExcel(selectedConferenceId);
            toast.success("File Excel Rekap berhasil diunduh!", { id: "export-recap" });
        } catch (error: any) {
            toast.error("Gagal mengunduh Excel Rekap: " + (error?.message || "Terjadi kesalahan"), { id: "export-recap" });
        } finally {
            setIsExporting(false);
        }
    };

    const handlePrint = () => {
        window.print();
    };

    // Kalkulasi persentase pembayaran
    const paymentRate = useMemo(() => {
        if (!summary?.total_billed_amount || summary.total_billed_amount === 0) return 0;
        return Math.min(100, Math.round((summary.paid_amount / summary.total_billed_amount) * 100));
    }, [summary]);

    // Kalkulasi persentase camera ready
    const cameraReadyRate = useMemo(() => {
        if (!summary?.total_proceeding || summary.total_proceeding === 0) return 0;
        return Math.min(100, Math.round((summary.camera_ready_uploaded / summary.total_proceeding) * 100));
    }, [summary]);

    return (
        <div className="space-y-6 animate-in fade-in duration-300 print:space-y-4">
            {/* Header Hero Banner (Clean, Calm, & Spacious) */}
            <div className="bg-gradient-to-r from-slate-900 via-blue-950 to-slate-900 rounded-3xl p-6 md:p-8 text-white shadow-xl border border-blue-900/30 flex flex-col md:flex-row md:items-center justify-between gap-4 print:hidden relative overflow-hidden">
                <div className="space-y-1.5 relative z-10 max-w-3xl">
                    <div className="flex items-center gap-2">
                        <span className="px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider bg-blue-500/20 text-blue-300 border border-blue-400/30 flex items-center gap-1.5">
                            <Sparkles className="w-3 h-3 text-amber-300" /> Ringkasan Rekap
                        </span>
                        <span className="text-xs text-slate-400 font-medium">Data Realtime</span>
                    </div>
                    <h2 className="text-2xl sm:text-3xl font-black tracking-tight text-white">
                        Dashboard Rekap Eksekutif Output Conference
                    </h2>
                    <p className="text-xs sm:text-sm text-slate-300 leading-relaxed">
                        Ikhtisar komprehensif penerimaan dana publikasi, kelengkapan naskah camera-ready, keputusan penulis, serta sebaran publisher & jurnal untuk kebutuhan rapat pimpinan.
                    </p>
                </div>
            </div>

            {/* Toolbar Kontrol Rapat (Diletakkan di Bawah Banner agar Lebih Rapi & Lapang) */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row sm:items-center justify-between gap-3 print:hidden">
                {/* Sisi Kiri: Filter Track Konferensi & Refresh */}
                <div className="flex items-center gap-2.5 flex-1 max-w-lg">
                    <div className="flex-1 min-w-[200px]">
                        <Select value={selectedConferenceId} onValueChange={setSelectedConferenceId}>
                            <SelectTrigger className="h-10 bg-slate-50 dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-800 dark:text-slate-100 rounded-xl text-xs sm:text-sm font-semibold shadow-2xs hover:border-slate-300 focus:ring-1 focus:ring-blue-500">
                                <GraduationCap className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400 shrink-0" />
                                <SelectValue placeholder="Pilih Track Konferensi" />
                            </SelectTrigger>
                            <SelectContent className="rounded-xl max-h-[300px]">
                                <SelectItem value="all">Semua Track Konferensi</SelectItem>
                                {conferences?.map((conf) => (
                                    <SelectItem key={conf.conference_id} value={String(conf.conference_id)}>
                                        {conf.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>

                    <TooltipProvider>
                        <Tooltip>
                            <TooltipTrigger asChild>
                                <Button
                                    variant="outline"
                                    size="icon"
                                    onClick={() => refetch()}
                                    disabled={isLoading || isFetching}
                                    className="h-10 w-10 shrink-0 rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-200"
                                >
                                    <RefreshCw className={`w-4 h-4 ${isFetching ? "animate-spin text-blue-600" : ""}`} />
                                </Button>
                            </TooltipTrigger>
                            <TooltipContent>Segarkan Data Realtime</TooltipContent>
                        </Tooltip>
                    </TooltipProvider>

                    <span className="text-xs text-slate-400 dark:text-slate-500 font-medium hidden md:inline shrink-0">
                        Status: <strong className="text-emerald-600 dark:text-emerald-400 font-semibold">Tersinkron</strong>
                    </span>
                </div>

                {/* Sisi Kanan: Cetak Semua Rekap & Export Rekap Excel */}
                <div className="flex items-center gap-2.5 flex-wrap justify-end">
                    <Button
                        variant="outline"
                        onClick={handlePrint}
                        className="h-10 px-4 rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-slate-700 dark:text-slate-200 shadow-2xs text-xs sm:text-sm"
                    >
                        <Printer className="w-4 h-4 mr-2 text-slate-500 dark:text-slate-400" />
                        <span>Cetak Semua Rekap</span>
                    </Button>

                    <Button
                        onClick={handleExportExcel}
                        disabled={isExporting}
                        className="h-10 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold shadow-md shadow-emerald-600/20 text-xs sm:text-sm transition-all"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        <span>{isExporting ? "Mengunduh Excel..." : "Export Rekap Excel"}</span>
                    </Button>
                </div>
            </div>

            {/* Print Header (Only visible when printing) */}
            <div className="hidden print:block border-b border-slate-300 pb-4 mb-4">
                <h1 className="text-xl font-bold text-slate-900">REKAPITULASI EKSEKUTIF OUTPUT CONFERENCE & PROSIDING</h1>
                <p className="text-xs text-slate-600">KONASPI XII 2026 — Dokumen Rapat Pimpinan & Reviewer</p>
                <p className="text-[10px] text-slate-500 mt-1">Dicetak pada: {new Date().toLocaleString("id-ID")}</p>
            </div>

            {/* SECTION 1: 4 KARTU HIGHLIGHT UTAMA (KPI CARDS) - Rapih, Lapang, & Bebas Overflow */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* 1. Pembayaran Lunas */}
                <div className="bg-gradient-to-br from-emerald-500/10 via-emerald-500/5 to-transparent dark:from-emerald-950/40 dark:via-emerald-900/10 rounded-3xl p-5 border border-emerald-200/80 dark:border-emerald-800/40 shadow-sm transition-all hover:shadow-md flex flex-col justify-between min-h-[210px]">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                                Pembayaran Lunas
                            </span>
                            <div className="p-2 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300">
                                <Wallet className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-baseline gap-1.5 flex-wrap">
                                <span className="text-xs sm:text-sm font-bold text-slate-500 dark:text-slate-400">Rp</span>
                                <span className="text-xl sm:text-2xl xl:text-[22px] 2xl:text-2xl font-black text-slate-900 dark:text-white tracking-tight break-normal" title={formatIDR(summary?.paid_amount)}>
                                    {formatNumber(summary?.paid_amount)}
                                </span>
                            </div>
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-snug">
                                <span className="font-bold text-emerald-600 dark:text-emerald-400">{formatNumber(summary?.paid_count)} paper</span> lunas ({paymentRate}% dari tagihan)
                            </p>
                        </div>
                    </div>
                    <div className="mt-auto pt-3 border-t border-emerald-100 dark:border-emerald-900/50 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500 dark:text-slate-400 truncate">{formatNumber(summary?.total_billings_count)} tagihan terbit</span>
                        <button
                            onClick={() => onDrillDown({ billing_status: "paid" })}
                            className="font-bold text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 flex items-center gap-1 group shrink-0"
                        >
                            <span>Lihat Data</span>
                            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </div>

                {/* 2. Total Paper Terpetakan */}
                <div className="bg-gradient-to-br from-blue-500/10 via-blue-500/5 to-transparent dark:from-blue-950/40 dark:via-blue-900/10 rounded-3xl p-5 border border-blue-200/80 dark:border-blue-800/40 shadow-sm transition-all hover:shadow-md flex flex-col justify-between min-h-[210px]">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-blue-700 dark:text-blue-400">
                                Total Paper Terpetakan
                            </span>
                            <div className="p-2 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300">
                                <FileText className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                                    {formatNumber(summary?.total_decisions)}
                                </span>
                                <span className="text-xs font-semibold text-slate-400">Naskah</span>
                            </div>
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-snug">
                                <span className="font-bold text-purple-600 dark:text-purple-400">{formatNumber(summary?.total_proceeding)} Prosiding</span> • <span className="font-bold text-blue-600 dark:text-blue-400">{formatNumber(summary?.total_journal)} Jurnal</span>
                            </p>
                        </div>
                    </div>
                    <div className="mt-auto pt-3 border-t border-blue-100 dark:border-blue-900/50 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500 dark:text-slate-400">100% teralokasi</span>
                        <button
                            onClick={() => onDrillDown({})}
                            className="font-bold text-blue-600 hover:text-blue-700 dark:text-blue-400 flex items-center gap-1 group shrink-0"
                        >
                            <span>Lihat Semua</span>
                            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </div>

                {/* 3. Camera Ready Terkumpul */}
                <div className="bg-gradient-to-br from-purple-500/10 via-purple-500/5 to-transparent dark:from-purple-950/40 dark:via-purple-900/10 rounded-3xl p-5 border border-purple-200/80 dark:border-purple-800/40 shadow-sm transition-all hover:shadow-md flex flex-col justify-between min-h-[210px]">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-purple-700 dark:text-purple-400">
                                Unggah Camera-Ready
                            </span>
                            <div className="p-2 rounded-xl bg-purple-100 text-purple-700 dark:bg-purple-900/60 dark:text-purple-300">
                                <FileCheck2 className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                                    {formatNumber(summary?.camera_ready_uploaded)}
                                </span>
                                <span className="text-xs font-semibold text-slate-400">Paper</span>
                            </div>
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-snug">
                                <span className="font-bold text-purple-600 dark:text-purple-400">{cameraReadyRate}%</span> dari {formatNumber(summary?.total_proceeding)} naskah prosiding
                            </p>
                        </div>
                    </div>
                    <div className="mt-auto pt-3 border-t border-purple-100 dark:border-purple-900/50 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500 dark:text-slate-400 truncate">{formatNumber(summary?.both_files_uploaded)} berkas lengkap</span>
                        <button
                            onClick={() => onDrillDown({ registration_status: "submitted" })}
                            className="font-bold text-purple-600 hover:text-purple-700 dark:text-purple-400 flex items-center gap-1 group shrink-0"
                        >
                            <span>Lihat Berkas</span>
                            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </div>

                {/* 4. Konfirmasi Penulis */}
                <div className="bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-transparent dark:from-amber-950/40 dark:via-amber-900/10 rounded-3xl p-5 border border-amber-200/80 dark:border-amber-800/40 shadow-sm transition-all hover:shadow-md flex flex-col justify-between min-h-[210px]">
                    <div>
                        <div className="flex items-center justify-between mb-3">
                            <span className="text-xs font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400">
                                Konfirmasi Penulis
                            </span>
                            <div className="p-2 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300">
                                <CheckCircle2 className="w-4 h-4" />
                            </div>
                        </div>
                        <div className="space-y-1">
                            <div className="flex items-baseline gap-1.5">
                                <span className="text-2xl sm:text-3xl font-black text-slate-900 dark:text-white tracking-tight">
                                    {formatNumber((summary?.agreed_journal ?? 0) + (summary?.declined_to_proceeding ?? 0) + (summary?.direct_proceeding ?? 0))}
                                </span>
                                <span className="text-xs font-semibold text-slate-400">Final</span>
                            </div>
                            <p className="text-xs font-medium text-slate-600 dark:text-slate-400 leading-snug">
                                <span className="font-bold text-amber-600 dark:text-amber-400">{formatNumber(summary?.pending_journal)} paper</span> pending konfirmasi
                            </p>
                        </div>
                    </div>
                    <div className="mt-auto pt-3 border-t border-amber-100 dark:border-amber-900/50 flex items-center justify-between text-[11px]">
                        <span className="text-slate-500 dark:text-slate-400 truncate">{formatNumber(summary?.declined_to_proceeding)} pindah prosiding</span>
                        <button
                            onClick={() => onDrillDown({ author_decision: "pending" })}
                            className="font-bold text-amber-600 hover:text-amber-700 dark:text-amber-400 flex items-center gap-1 group shrink-0"
                        >
                            <span>Lihat Pending</span>
                            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* SECTION 2: REKAPITULASI KEUANGAN & TAGIHAN DETAIL */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 shadow-sm border border-slate-200 dark:border-slate-800 space-y-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-4 border-b border-slate-100 dark:border-slate-800">
                    <div>
                        <h3 className="text-lg font-black text-slate-900 dark:text-white flex items-center gap-2">
                            <Wallet className="w-5 h-5 text-emerald-600" />
                            <span>Rekapitulasi Keuangan & Tagihan Publikasi Prosiding</span>
                        </h3>
                        <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">
                            Ringkasan peredaran invoice, penerimaan dana masuk, tagihan pending, dan tagihan kadaluarsa yang membutuhkan tindak lanjut.
                        </p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        <span className="text-xs font-semibold px-3 py-1 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/50 rounded-xl">
                            Realisasi: {paymentRate}%
                        </span>
                    </div>
                </div>

                {/* Progress Bar Nominal */}
                <div className="space-y-2">
                    <div className="flex justify-between text-xs font-semibold">
                        <span className="text-slate-600 dark:text-slate-300">
                            Total Tagihan Diterbitkan: <strong className="text-slate-900 dark:text-white">{formatIDR(summary?.total_billed_amount)}</strong> ({formatNumber(summary?.total_billings_count)} invoice)
                        </span>
                        <span className="text-emerald-600 dark:text-emerald-400 font-bold">
                            Lunas: {formatIDR(summary?.paid_amount)} ({paymentRate}%)
                        </span>
                    </div>
                    <div className="h-3 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden flex shadow-inner">
                        <div
                            style={{ width: `${paymentRate}%` }}
                            className="bg-emerald-500 h-full rounded-l-full transition-all duration-500"
                            title={`Lunas: ${formatIDR(summary?.paid_amount)}`}
                        />
                        <div
                            style={{ width: `${summary?.total_billed_amount ? ((summary.unpaid_amount / summary.total_billed_amount) * 100) : 0}%` }}
                            className="bg-amber-500 h-full transition-all duration-500"
                            title={`Unpaid: ${formatIDR(summary?.unpaid_amount)}`}
                        />
                        <div
                            style={{ width: `${summary?.total_billed_amount ? ((summary.expired_amount / summary.total_billed_amount) * 100) : 0}%` }}
                            className="bg-rose-500 h-full rounded-r-full transition-all duration-500"
                            title={`Expired: ${formatIDR(summary?.expired_amount)}`}
                        />
                    </div>
                    <div className="flex items-center gap-4 text-[11px] text-slate-500 dark:text-slate-400 flex-wrap pt-1">
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 inline-block" />
                            <span>Lunas ({formatNumber(summary?.paid_count)} paper)</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 inline-block" />
                            <span>Belum Dibayar ({formatNumber(summary?.unpaid_count)} paper)</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 inline-block" />
                            <span>Kadaluarsa ({formatNumber(summary?.expired_count)} paper)</span>
                        </div>
                    </div>
                </div>

                {/* 5 Kartu Status Keuangan - Responsif & Terhindar dari Text Overflow */}
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3.5 pt-2">
                    {/* 1. Lunas */}
                    <div
                        onClick={() => onDrillDown({ billing_status: "paid" })}
                        className="cursor-pointer p-4 rounded-2xl bg-emerald-50/70 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/40 hover:bg-emerald-100/70 transition-all space-y-1.5"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-emerald-800 dark:text-emerald-300">Lunas (Paid)</span>
                            <Check className="w-4 h-4 text-emerald-600" />
                        </div>
                        <div className="text-base sm:text-lg xl:text-xl font-black text-emerald-900 dark:text-emerald-200 tracking-tight truncate" title={formatIDR(summary?.paid_amount)}>
                            {formatIDR(summary?.paid_amount)}
                        </div>
                        <div className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                            {formatNumber(summary?.paid_count)} transaksi lunas
                        </div>
                    </div>

                    {/* 2. Belum Dibayar (Unpaid) */}
                    <div
                        onClick={() => onDrillDown({ billing_status: "unpaid" })}
                        className="cursor-pointer p-4 rounded-2xl bg-amber-50/70 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/40 hover:bg-amber-100/70 transition-all space-y-1.5"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-amber-800 dark:text-amber-300">Belum Bayar (Unpaid)</span>
                            <Clock className="w-4 h-4 text-amber-600" />
                        </div>
                        <div className="text-base sm:text-lg xl:text-xl font-black text-amber-900 dark:text-amber-200 tracking-tight truncate" title={formatIDR(summary?.unpaid_amount)}>
                            {formatIDR(summary?.unpaid_amount)}
                        </div>
                        <div className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                            {formatNumber(summary?.unpaid_count)} transaksi aktif
                        </div>
                    </div>

                    {/* 3. Kadaluarsa (Expired) */}
                    <div
                        onClick={() => onDrillDown({ billing_status: "expired" })}
                        className="cursor-pointer p-4 rounded-2xl bg-rose-50/70 dark:bg-rose-950/20 border border-rose-200 dark:border-rose-800/40 hover:bg-rose-100/70 transition-all space-y-1.5"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-rose-800 dark:text-rose-300">Kadaluarsa (Expired)</span>
                            <AlertTriangle className="w-4 h-4 text-rose-600" />
                        </div>
                        <div className="text-base sm:text-lg xl:text-xl font-black text-rose-900 dark:text-rose-200 tracking-tight truncate" title={formatIDR(summary?.expired_amount)}>
                            {formatIDR(summary?.expired_amount)}
                        </div>
                        <div className="text-xs text-rose-700 dark:text-rose-400 font-medium">
                            {formatNumber(summary?.expired_count)} butuh follow up
                        </div>
                    </div>

                    {/* 4. Siap Dibuatkan Tagihan (Ready to Bill) */}
                    <div
                        onClick={() => onDrillDown({ billing_status: "ready_to_bill" })}
                        className="cursor-pointer p-4 rounded-2xl bg-blue-50/70 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/40 hover:bg-blue-100/70 transition-all space-y-1.5"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-blue-800 dark:text-blue-300">Siap Buat Tagihan</span>
                            <FileCheck2 className="w-4 h-4 text-blue-600" />
                        </div>
                        <div className="text-base sm:text-lg xl:text-xl font-black text-blue-900 dark:text-blue-200 tracking-tight truncate">
                            {formatNumber(summary?.ready_to_bill_count)} Paper
                        </div>
                        <div className="text-xs text-blue-700 dark:text-blue-400 font-medium truncate">
                            Berkas lengkap, belum tagihan
                        </div>
                    </div>

                    {/* 5. Belum Ada Tagihan */}
                    <div
                        onClick={() => onDrillDown({ billing_status: "no_billing", output_route: "proceeding" })}
                        className="cursor-pointer p-4 rounded-2xl bg-slate-100/80 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:bg-slate-200/60 transition-all space-y-1.5"
                    >
                        <div className="flex items-center justify-between">
                            <span className="text-xs font-bold text-slate-700 dark:text-slate-300">Belum Ada Tagihan</span>
                            <Clock className="w-4 h-4 text-slate-500" />
                        </div>
                        <div className="text-base sm:text-lg xl:text-xl font-black text-slate-800 dark:text-slate-200 tracking-tight truncate">
                            {formatNumber(summary?.no_billing_count)} Paper
                        </div>
                        <div className="text-xs text-slate-500 dark:text-slate-400 font-medium truncate">
                            Naskah belum submit berkas
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 3: STATUS ALUR KEPUTUSAN & KELENGKAPAN BERKAS (SPLIT 2 COLUMNS) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Kolom Kiri: Alur Keputusan Penulis */}
                <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 shadow-sm border border-slate-200 dark:border-slate-800 space-y-5">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                        <div className="space-y-0.5">
                            <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                                <CheckCircle2 className="w-5 h-5 text-blue-600" />
                                <span>Status Respon / Keputusan Penulis</span>
                            </h3>
                            <p className="text-xs text-slate-500">
                                Pilihan penulis atas rekomendasi jalur publikasi (Jurnal vs Prosiding).
                            </p>
                        </div>
                    </div>

                    <div className="space-y-3">
                        {/* 1. Setuju Jurnal */}
                        <div
                            onClick={() => onDrillDown({ output_route: "journal", author_decision: "agreed" })}
                            className="cursor-pointer p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 flex items-center justify-between transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-emerald-100 text-emerald-700 dark:bg-emerald-900/60 dark:text-emerald-300 flex items-center justify-center font-bold text-sm">
                                    <Check className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Setuju ke Jurnal (Agreed)</h4>
                                    <p className="text-xs text-slate-500">Penulis menerima rekomendasi jurnal Scopus/Sinta</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-lg font-black text-slate-900 dark:text-white">{formatNumber(summary?.agreed_journal)}</span>
                                <span className="block text-[11px] text-emerald-600 font-semibold">Final</span>
                            </div>
                        </div>

                        {/* 2. Pindah ke Prosiding */}
                        <div
                            onClick={() => onDrillDown({ output_route: "proceeding", author_decision: "declined" })}
                            className="cursor-pointer p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 flex items-center justify-between transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-purple-100 text-purple-700 dark:bg-purple-900/60 dark:text-purple-300 flex items-center justify-center font-bold text-sm">
                                    <ArrowRightCircle className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Memilih Prosiding (Declined Jurnal)</h4>
                                    <p className="text-xs text-slate-500">Penulis menolak tawaran jurnal & dialihkan ke prosiding</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-lg font-black text-slate-900 dark:text-white">{formatNumber(summary?.declined_to_proceeding)}</span>
                                <span className="block text-[11px] text-purple-600 font-semibold">Pindah Jalur</span>
                            </div>
                        </div>

                        {/* 3. Direct Prosiding */}
                        <div
                            onClick={() => onDrillDown({ output_route: "proceeding", author_decision: "agreed" })}
                            className="cursor-pointer p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 border border-slate-200/80 dark:border-slate-700 flex items-center justify-between transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300 flex items-center justify-center font-bold text-sm">
                                    <Layers className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Direct Prosiding (Pemetaan Awal)</h4>
                                    <p className="text-xs text-slate-500">Sejak awal langsung direkomendasikan ke prosiding</p>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-lg font-black text-slate-900 dark:text-white">{formatNumber(summary?.direct_proceeding)}</span>
                                <span className="block text-[11px] text-blue-600 font-semibold">Prosiding Asli</span>
                            </div>
                        </div>

                        {/* 4. Menunggu Konfirmasi (Pending) */}
                        <div
                            onClick={() => onDrillDown({ author_decision: "pending" })}
                            className="cursor-pointer p-3.5 rounded-2xl bg-amber-50/60 dark:bg-amber-950/20 hover:bg-amber-100/60 border border-amber-200/80 dark:border-amber-800/50 flex items-center justify-between transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-xl bg-amber-100 text-amber-700 dark:bg-amber-900/60 dark:text-amber-300 flex items-center justify-center font-bold text-sm">
                                    <Clock className="w-4 h-4" />
                                </div>
                                <div>
                                    <h4 className="text-sm font-bold text-slate-900 dark:text-white">Menunggu Konfirmasi (Pending)</h4>
                                    <p className="text-xs text-amber-700 dark:text-amber-400 font-medium">
                                        {formatNumber(summary?.pending_journal_expired)} paper telah melewati deadline keputusan!
                                    </p>
                                </div>
                            </div>
                            <div className="text-right">
                                <span className="text-lg font-black text-amber-900 dark:text-amber-200">{formatNumber(summary?.pending_journal)}</span>
                                <span className="block text-[11px] text-amber-700 dark:text-amber-400 font-semibold">Perlu Follow-up</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Kolom Kanan: Status Berkas Prosiding */}
                <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 shadow-sm border border-slate-200 dark:border-slate-800 space-y-5">
                    <div className="flex items-center justify-between pb-3 border-b border-slate-100 dark:border-slate-800">
                        <div className="space-y-0.5">
                            <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                                <FileCheck2 className="w-5 h-5 text-purple-600" />
                                <span>Kelengkapan Berkas Prosiding (596 Paper)</span>
                            </h3>
                            <p className="text-xs text-slate-500">
                                Progres pengumpulan naskah camera-ready & formulir hak cipta (copyright).
                            </p>
                        </div>
                    </div>

                    {/* Progress Bar Kelengkapan */}
                    <div className="space-y-2">
                        <div className="flex justify-between text-xs font-semibold">
                            <span className="text-slate-600 dark:text-slate-300">Capaian Naskah Camera-Ready:</span>
                            <span className="font-bold text-purple-600 dark:text-purple-400">
                                {formatNumber(summary?.camera_ready_uploaded)} dari {formatNumber(summary?.total_proceeding)} paper ({cameraReadyRate}%)
                            </span>
                        </div>
                        <div className="h-2.5 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                            <div
                                style={{ width: `${cameraReadyRate}%` }}
                                className="bg-purple-600 h-full rounded-full transition-all duration-500"
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 pt-1">
                        <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700">
                            <span className="text-xs text-slate-500 block mb-1">Camera-Ready Diunggah</span>
                            <span className="text-xl font-black text-slate-900 dark:text-white">{formatNumber(summary?.camera_ready_uploaded)}</span>
                            <span className="text-[11px] text-purple-600 block mt-0.5 font-semibold">{cameraReadyRate}% dari target</span>
                        </div>
                        <div className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700">
                            <span className="text-xs text-slate-500 block mb-1">Copyright Form Diunggah</span>
                            <span className="text-xl font-black text-slate-900 dark:text-white">{formatNumber(summary?.copyright_form_uploaded)}</span>
                            <span className="text-[11px] text-blue-600 block mt-0.5 font-semibold">Form Hak Cipta</span>
                        </div>
                    </div>

                    {/* Rincian Status Formulir Registrasi */}
                    <div className="space-y-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500">Rincian Status Formulir</span>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-center">
                            <div
                                onClick={() => onDrillDown({ registration_status: "draft" })}
                                className="cursor-pointer p-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800/30"
                            >
                                <span className="text-[11px] text-amber-700 dark:text-amber-300 font-semibold block">Draft (Mengisi)</span>
                                <span className="text-base font-black text-amber-900 dark:text-amber-200">{formatNumber(summary?.reg_draft)}</span>
                            </div>
                            <div
                                onClick={() => onDrillDown({ registration_status: "submitted" })}
                                className="cursor-pointer p-2.5 rounded-xl bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800/30"
                            >
                                <span className="text-[11px] text-blue-700 dark:text-blue-300 font-semibold block">Submitted Final</span>
                                <span className="text-base font-black text-blue-900 dark:text-blue-200">{formatNumber(summary?.reg_submitted)}</span>
                            </div>
                            <div
                                onClick={() => onDrillDown({ billing_status: "unpaid" })}
                                className="cursor-pointer p-2.5 rounded-xl bg-indigo-50 dark:bg-indigo-950/20 border border-indigo-200 dark:border-indigo-800/30"
                            >
                                <span className="text-[11px] text-indigo-700 dark:text-indigo-300 font-semibold block">Tunggu Bayar</span>
                                <span className="text-base font-black text-indigo-900 dark:text-indigo-200">{formatNumber(summary?.reg_payment_pending)}</span>
                            </div>
                            <div
                                onClick={() => onDrillDown({ billing_status: "paid" })}
                                className="cursor-pointer p-2.5 rounded-xl bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800/30"
                            >
                                <span className="text-[11px] text-emerald-700 dark:text-emerald-300 font-semibold block">Lunas (Paid)</span>
                                <span className="text-base font-black text-emerald-900 dark:text-emerald-200">{formatNumber(summary?.reg_paid)}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* SECTION 4: REKAP PUBLISHER PROSIDING (KNE vs SHS) */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 shadow-sm border border-slate-200 dark:border-slate-800 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                            <Layers className="w-5 h-5 text-purple-600" />
                            <span>Rekapitulasi per Publisher Prosiding Internasional</span>
                        </h3>
                        <p className="text-xs text-slate-500">
                            Distribusi alokasi naskah ke publisher KnE Publishing dan SHS Web of Conferences.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {byPublisher.map((pub) => {
                        const pubCrRate = pub.total_papers > 0 ? Math.round((pub.camera_ready_count / pub.total_papers) * 100) : 0;
                        const pubPayRate = (pub.paid_amount + pub.unpaid_amount + pub.expired_amount) > 0
                            ? Math.round((pub.paid_amount / (pub.paid_amount + pub.unpaid_amount + pub.expired_amount)) * 100)
                            : 0;

                        return (
                            <div
                                key={pub.code}
                                className="p-5 rounded-2xl border border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-800/30 space-y-4 hover:border-purple-300 dark:hover:border-purple-700 transition-colors"
                            >
                                <div className="flex items-start justify-between">
                                    <div className="space-y-1">
                                        <div className="flex items-center gap-2">
                                            <span className="px-2.5 py-0.5 rounded-lg bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 font-black text-xs">
                                                {pub.code}
                                            </span>
                                            <h4 className="font-bold text-slate-900 dark:text-white text-base">
                                                {pub.name}
                                            </h4>
                                        </div>
                                        <p className="text-xs text-slate-500">
                                            Total Alokasi: <strong className="text-slate-800 dark:text-slate-200 font-bold">{formatNumber(pub.total_papers)} Paper</strong>
                                        </p>
                                    </div>
                                    <div className="text-right">
                                        <span className="text-lg font-black text-emerald-600 dark:text-emerald-400">
                                            {formatIDR(pub.paid_amount)}
                                        </span>
                                        <span className="block text-[11px] text-slate-400">
                                            {formatNumber(pub.paid_count)} Lunas ({pubPayRate}%)
                                        </span>
                                    </div>
                                </div>

                                {/* Mini Progress Bar Camera Ready */}
                                <div className="space-y-1.5">
                                    <div className="flex justify-between text-xs">
                                        <span className="text-slate-500">Camera-Ready Terkumpul:</span>
                                        <span className="font-bold text-purple-600 dark:text-purple-400">
                                            {formatNumber(pub.camera_ready_count)} paper ({pubCrRate}%)
                                        </span>
                                    </div>
                                    <div className="h-2 w-full bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                                        <div
                                            style={{ width: `${pubCrRate}%` }}
                                            className="bg-purple-600 h-full rounded-full"
                                        />
                                    </div>
                                </div>

                                {/* Metrik Grid */}
                                <div className="grid grid-cols-3 gap-2 text-center pt-1 text-xs">
                                    <div className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700">
                                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">Berkas Lengkap</span>
                                        <span className="font-bold text-slate-800 dark:text-slate-200">{formatNumber(pub.both_files_count)}</span>
                                    </div>
                                    <div className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700">
                                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">Tagihan Terbit</span>
                                        <span className="font-bold text-slate-800 dark:text-slate-200">{formatNumber(pub.billings_count)}</span>
                                    </div>
                                    <div className="p-2 rounded-xl bg-white dark:bg-slate-800 border border-slate-200/80 dark:border-slate-700">
                                        <span className="text-[10px] text-slate-400 uppercase font-semibold block">Siap Ditagih</span>
                                        <span className="font-bold text-blue-600 dark:text-blue-400">{formatNumber(pub.ready_to_bill_count)}</span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* SECTION 5: REKAP JURNAL TARGET (SINTA & SCOPUS) */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 shadow-sm border border-slate-200 dark:border-slate-800 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                            <BookOpen className="w-5 h-5 text-blue-600" />
                            <span>Rekapitulasi Target Jurnal Rekomendasi (438 Paper)</span>
                        </h3>
                        <p className="text-xs text-slate-500">
                            Distribusi naskah yang ditawarkan ke Jurnal Sinta dan Jurnal Terindeks Scopus.
                        </p>
                    </div>

                    <div className="flex items-center gap-1.5 p-1 bg-slate-100 dark:bg-slate-800 rounded-xl">
                        <button
                            onClick={() => setJournalSubTab("sinta")}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                                journalSubTab === "sinta"
                                    ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                            }`}
                        >
                            Jurnal Sinta ({bySinta?.journals?.length ?? 0})
                        </button>
                        <button
                            onClick={() => setJournalSubTab("scopus")}
                            className={`px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${
                                journalSubTab === "scopus"
                                    ? "bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm"
                                    : "text-slate-600 dark:text-slate-400 hover:text-slate-900"
                            }`}
                        >
                            Jurnal Scopus ({byScopus?.journals?.length ?? 0})
                        </button>
                    </div>
                </div>

                {/* Sub Tab: Sinta */}
                {journalSubTab === "sinta" && (
                    <div className="space-y-4">
                        {/* Peringkat Sinta Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5">
                            {bySinta?.by_rank?.map((r) => (
                                <div
                                    key={r.sinta_rank}
                                    className="p-3 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700 text-center"
                                >
                                    <span className="text-[11px] font-bold text-slate-600 dark:text-slate-400 block">{r.sinta_rank}</span>
                                    <span className="text-xl font-black text-slate-900 dark:text-white block mt-0.5">{formatNumber(r.total)}</span>
                                    <div className="flex items-center justify-center gap-1.5 text-[10px] text-slate-500 mt-1">
                                        <span className="text-emerald-600 font-semibold">{r.agreed}✓</span>
                                        <span>•</span>
                                        <span className="text-amber-600 font-semibold">{r.pending}⌛</span>
                                        <span>•</span>
                                        <span className="text-purple-600 font-semibold">{r.declined}⤶</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Tabel Detail Jurnal Sinta */}
                        <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                            <table className="w-full text-xs text-left">
                                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="py-2.5 px-3.5">Nama Jurnal</th>
                                        <th className="py-2.5 px-3">Peringkat</th>
                                        <th className="py-2.5 px-3">Editor in Chief</th>
                                        <th className="py-2.5 px-3 text-center">Total Naskah</th>
                                        <th className="py-2.5 px-3 text-center">Setuju</th>
                                        <th className="py-2.5 px-3 text-center">Pending</th>
                                        <th className="py-2.5 px-3 text-center">Pindah Prosiding</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {bySinta?.journals?.map((j) => (
                                        <tr key={j.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                                            <td className="py-2.5 px-3.5 font-semibold text-slate-800 dark:text-slate-200">{j.journal_name}</td>
                                            <td className="py-2.5 px-3">
                                                <span className="px-2 py-0.5 rounded-md bg-blue-50 dark:bg-blue-950/40 text-blue-700 dark:text-blue-300 font-bold text-[10px]">
                                                    {j.sinta_rank || "-"}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-3 text-slate-500">{j.editor_in_chief || "-"}</td>
                                            <td className="py-2.5 px-3 text-center font-bold text-slate-800 dark:text-slate-200">{j.total}</td>
                                            <td className="py-2.5 px-3 text-center font-semibold text-emerald-600">{j.agreed}</td>
                                            <td className="py-2.5 px-3 text-center font-semibold text-amber-600">{j.pending}</td>
                                            <td className="py-2.5 px-3 text-center font-semibold text-purple-600">{j.declined}</td>
                                        </tr>
                                    ))}
                                    {(!bySinta?.journals || bySinta.journals.length === 0) && (
                                        <tr>
                                            <td colSpan={7} className="py-6 text-center text-slate-400">Tidak ada data jurnal Sinta pada track yang dipilih.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {/* Sub Tab: Scopus */}
                {journalSubTab === "scopus" && (
                    <div className="space-y-4">
                        {/* Quartile Cards */}
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {byScopus?.by_quartile?.map((q) => (
                                <div
                                    key={q.quartile}
                                    className="p-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/40 border border-slate-200/80 dark:border-slate-700 text-center"
                                >
                                    <span className="text-xs font-bold text-purple-700 dark:text-purple-400 block">{q.quartile}</span>
                                    <span className="text-2xl font-black text-slate-900 dark:text-white block mt-0.5">{formatNumber(q.total)}</span>
                                    <div className="flex items-center justify-center gap-1.5 text-xs text-slate-500 mt-1">
                                        <span className="text-emerald-600 font-semibold">{q.agreed} Setuju</span>
                                        <span>•</span>
                                        <span className="text-amber-600 font-semibold">{q.pending} Pending</span>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Tabel Detail Jurnal Scopus */}
                        <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                            <table className="w-full text-xs text-left">
                                <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200 dark:border-slate-800">
                                    <tr>
                                        <th className="py-2.5 px-3.5">Nama Jurnal Scopus</th>
                                        <th className="py-2.5 px-3">Quartile</th>
                                        <th className="py-2.5 px-3">Editor in Chief</th>
                                        <th className="py-2.5 px-3 text-center">Total Naskah</th>
                                        <th className="py-2.5 px-3 text-center">Setuju</th>
                                        <th className="py-2.5 px-3 text-center">Pending</th>
                                        <th className="py-2.5 px-3 text-center">Pindah Prosiding</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                    {byScopus?.journals?.map((j) => (
                                        <tr key={j.id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                                            <td className="py-2.5 px-3.5 font-semibold text-slate-800 dark:text-slate-200">{j.journal_name}</td>
                                            <td className="py-2.5 px-3">
                                                <span className="px-2 py-0.5 rounded-md bg-purple-50 dark:bg-purple-950/40 text-purple-700 dark:text-purple-300 font-bold text-[10px]">
                                                    {j.quartile || "-"}
                                                </span>
                                            </td>
                                            <td className="py-2.5 px-3 text-slate-500">{j.editor_in_chief || "-"}</td>
                                            <td className="py-2.5 px-3 text-center font-bold text-slate-800 dark:text-slate-200">{j.total}</td>
                                            <td className="py-2.5 px-3 text-center font-semibold text-emerald-600">{j.agreed}</td>
                                            <td className="py-2.5 px-3 text-center font-semibold text-amber-600">{j.pending}</td>
                                            <td className="py-2.5 px-3 text-center font-semibold text-purple-600">{j.declined}</td>
                                        </tr>
                                    ))}
                                    {(!byScopus?.journals || byScopus.journals.length === 0) && (
                                        <tr>
                                            <td colSpan={7} className="py-6 text-center text-slate-400">Tidak ada data jurnal Scopus pada track yang dipilih.</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* SECTION 6: REKAP PER TRACK KONFERENSI */}
            <div className="bg-white dark:bg-slate-900 rounded-3xl p-6 sm:p-7 shadow-sm border border-slate-200 dark:border-slate-800 space-y-5">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-3 border-b border-slate-100 dark:border-slate-800">
                    <div>
                        <h3 className="text-base font-black text-slate-900 dark:text-white flex items-center gap-2">
                            <GraduationCap className="w-5 h-5 text-blue-600" />
                            <span>Rekapitulasi per Track Konferensi ({byConference.length} Track)</span>
                        </h3>
                        <p className="text-xs text-slate-500">
                            Perbandingan alokasi jalur, kelengkapan berkas, dan penerimaan pembayaran per track konferensi.
                        </p>
                    </div>
                </div>

                <div className="overflow-x-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                    <table className="w-full text-xs text-left">
                        <thead className="bg-slate-50 dark:bg-slate-800/60 text-slate-500 font-bold uppercase tracking-wider text-[10px] border-b border-slate-200 dark:border-slate-800">
                            <tr>
                                <th className="py-3 px-3.5">Track Konferensi</th>
                                <th className="py-3 px-3 text-center">Total Paper</th>
                                <th className="py-3 px-3 text-center">Jalur Jurnal</th>
                                <th className="py-3 px-3 text-center">Jalur Prosiding</th>
                                <th className="py-3 px-3 text-center">Camera-Ready</th>
                                <th className="py-3 px-3 text-center">Lunas (Paper)</th>
                                <th className="py-3 px-3 text-right">Pembayaran Masuk</th>
                                <th className="py-3 px-3 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {byConference.map((conf) => (
                                <tr key={conf.conference_id} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/40">
                                    <td className="py-3 px-3.5 font-semibold text-slate-900 dark:text-white">
                                        {conf.conference_name}
                                    </td>
                                    <td className="py-3 px-3 text-center font-bold text-slate-900 dark:text-white">
                                        {formatNumber(conf.total_papers)}
                                    </td>
                                    <td className="py-3 px-3 text-center text-slate-600 dark:text-slate-300">
                                        <span className="font-bold text-blue-600">{formatNumber(conf.journal_total)}</span>
                                        <span className="text-[10px] text-slate-400 block font-normal">
                                            ({conf.journal_agreed}✓ / {conf.journal_pending}⌛)
                                        </span>
                                    </td>
                                    <td className="py-3 px-3 text-center text-slate-600 dark:text-slate-300">
                                        <span className="font-bold text-purple-600">{formatNumber(conf.proceeding_total)}</span>
                                    </td>
                                    <td className="py-3 px-3 text-center font-semibold text-slate-700 dark:text-slate-300">
                                        {formatNumber(conf.camera_ready_count)}
                                    </td>
                                    <td className="py-3 px-3 text-center">
                                        <span className="px-2 py-0.5 rounded-md bg-emerald-50 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300 font-bold text-[10px]">
                                            {formatNumber(conf.paid_count)} Lunas
                                        </span>
                                    </td>
                                    <td className="py-3 px-3 text-right font-black text-emerald-600 dark:text-emerald-400">
                                        {formatIDR(conf.paid_amount)}
                                    </td>
                                    <td className="py-3 px-3 text-center">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => onDrillDown({ conference_id: conf.conference_id })}
                                            className="h-7 px-2 text-[11px] font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400"
                                        >
                                            Buka Track
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* SECTION 7: STATUS EMAIL NOTIFIKASI */}
            <div className="bg-slate-50 dark:bg-slate-800/40 rounded-3xl p-6 border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-2xl bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-300 flex items-center justify-center shrink-0">
                        <Mail className="w-5 h-5" />
                    </div>
                    <div>
                        <h4 className="text-sm font-bold text-slate-900 dark:text-white">
                            Status Pengiriman Email Notifikasi Keputusan
                        </h4>
                        <p className="text-xs text-slate-500">
                            {formatNumber(summary?.email_sent)} dari {formatNumber(summary?.total_decisions)} penulis telah menerima email notifikasi resmi.
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2 self-start sm:self-auto">
                    <span className="px-3 py-1 rounded-xl bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 font-bold text-xs flex items-center gap-1.5">
                        <Check className="w-3.5 h-3.5" />
                        {formatNumber(summary?.email_sent)} Terkirim
                    </span>
                    {Number(summary?.email_unsent ?? 0) > 0 && (
                        <span className="px-3 py-1 rounded-xl bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 font-bold text-xs flex items-center gap-1.5">
                            <Clock className="w-3.5 h-3.5" />
                            {formatNumber(summary?.email_unsent)} Belum Terkirim
                        </span>
                    )}
                </div>
            </div>

            {/* Action Bar Bawah (Cetak & Export di Bagian Bawah untuk Kemudahan Rapat) */}
            <div className="bg-white dark:bg-slate-900 rounded-2xl p-4 shadow-sm border border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 print:hidden">
                <div className="flex items-center gap-2 text-xs text-slate-500 dark:text-slate-400">
                    <Sparkles className="w-4 h-4 text-amber-500 shrink-0" />
                    <span>Dokumen rekap eksekutif output conference siap dicetak atau diexport ke Excel untuk laporan rapat.</span>
                </div>
                <div className="flex items-center gap-2.5 flex-wrap justify-end">
                    <Button
                        variant="outline"
                        onClick={handlePrint}
                        className="h-9 px-4 rounded-xl border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 font-semibold text-xs text-slate-700 dark:text-slate-200 shadow-2xs"
                    >
                        <Printer className="w-3.5 h-3.5 mr-2 text-slate-500" />
                        <span>Cetak Semua Rekap</span>
                    </Button>
                    <Button
                        onClick={handleExportExcel}
                        disabled={isExporting}
                        className="h-9 px-4 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-semibold text-xs shadow-md shadow-emerald-600/20"
                    >
                        <Download className="w-3.5 h-3.5 mr-2" />
                        <span>{isExporting ? "Mengunduh Excel..." : "Export Rekap Excel"}</span>
                    </Button>
                </div>
            </div>
        </div>
    );
}
